// Bridges the web's localStorage-backed `routeStyleSettings` with the
// server's `User.routeStyleSettings` JSONB field, via GetUser / UpdateUser.
//
// Conflict model (per the agreed product rule): server wins. On every
// `user:updated` we unconditionally overwrite localStorage with whatever
// the server holds; local edits trigger a debounced UpdateUser mutation.
//
// Boundary mappings (web ↔ cloud) — kept in lock-step with the iOS and
// Android cloud-sync helpers:
//   * web `main`/`alt`/`spur`/`recording` ↔ cloud `route`/`alt`/`spur`/`recording`
//   * cloud `transport` (iOS-only) is round-tripped on the server, ignored on web
//   * `thickness` (web) ↔ `thickness_multiplier` (cloud) — identity, both 0.5–3.0
//   * `opacity` (web 10–100 percent) ↔ `opacity` (cloud 0.1–1.0) via ×100 / ÷100
//   * `hideMileMarkers` (web global bool) is sourced from cloud["route"].hide_mile_markers
//     and pushed back to all four cloud route_types so iOS/Android see a single value

import { getAuth } from 'firebase/auth';
import emitter from './bus';
import { authReady } from './firebase/authReady';
import graphClient from './graphql/graph_client';
import { GetUser, UpdateUser } from './graphql/user-graph';
import {
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  ROUTE_STYLE_STORAGE_KEY,
  RouteStyleSettings,
  RouteTypePrefs,
} from './routeStyleSettings';
import {
  CloudRouteType,
  cloudTypeForOverlayType,
  cloudTypeForWebTab,
  opacityFromCloud,
  opacityToCloud,
  overlayTypeForCloudType,
  WEB_TO_CLOUD,
  WebTab,
  webTabForCloudType,
} from './routeStyleSettingsCloudMapping';
import {
  getOverlayLineStyles,
  OVERLAY_LINE_TYPES,
  OverlayLineStyles,
  saveOverlayLineStyles,
} from './overlayLineStyles';
import {
  CloudHikeListOverlaySettings,
  HikeListOverlaySettings,
  hikeListOverlaySettingsFromCloud,
  hikeListOverlaySettingsToCloud,
  hikeListStylesFromCloud,
  hikeListStylesToCloud,
  readHikeListOverlaySettings,
  readHikeListStyles,
  sameHikeListOverlaySettings,
  writeHikeListOverlaySettings,
  writeHikeListStyles,
} from './hikeListOverlay';

interface CloudRouteStyleSetting {
  routeType: CloudRouteType | string;
  color?: string | null;
  thicknessMultiplier?: number | null;
  opacity?: number | null;
  hideMileMarkers?: boolean | null;
}

let debounceHandle: number | null = null;
// The Hike lists overlay's on/off + selection go out in their own UpdateUser,
// so a toggle never re-sends every line style (and vice versa).
let hikeListOverlayDebounce: number | null = null;
let pendingHikeListOverlay: HikeListOverlaySettings | null = null;
let initialised = false;
// Set while we're mutating localStorage from a server response so the
// subsequent `route-style-settings:changed` we emit (which the map style
// layers also listen to) doesn't loop back into an immediate UpdateUser.
let suppressNextPush = false;
// Same guard for the overlay line styles, which ride the same GetUser payload
// but have their own `overlay-line-styles:changed` event.
let suppressNextOverlayPush = false;

export function applyFromUser(user: { routeStyleSettings?: CloudRouteStyleSetting[] | null } | null | undefined) {
  if (!user || !user.routeStyleSettings || user.routeStyleSettings.length === 0) return;

  const next: RouteStyleSettings = {
    main:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.main },
    alt:       { ...DEFAULT_ROUTE_STYLE_SETTINGS.alt },
    spur:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.spur },
    recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.recording },
    hideMileMarkers: DEFAULT_ROUTE_STYLE_SETTINGS.hideMileMarkers,
  };

  // Start from current local so any route_type the server doesn't return
  // (e.g. the user only set `main` from another platform) keeps its existing
  // local value rather than silently snapping back to default.
  const current = getRouteStyleSettings();
  next.main = { ...next.main, ...current.main };
  next.alt = { ...next.alt, ...current.alt };
  next.spur = { ...next.spur, ...current.spur };
  next.recording = { ...next.recording, ...current.recording };
  next.hideMileMarkers = current.hideMileMarkers;

  let sawHideMileMarkers = false;
  for (const entry of user.routeStyleSettings) {
    const tab = webTabForCloudType(entry.routeType);
    if (!tab) continue;
    const prefs: RouteTypePrefs = { ...next[tab] };
    if (entry.color != null) prefs.color = entry.color;
    if (entry.thicknessMultiplier != null) prefs.thickness = entry.thicknessMultiplier;
    if (entry.opacity != null) prefs.opacity = opacityFromCloud(entry.opacity);
    next[tab] = prefs;
    // Prefer `route` for the global flag (`main` on web). Fall back to any
    // non-null value so this still works if a client only ever pushed `alt`.
    if (entry.hideMileMarkers != null) {
      if (entry.routeType?.toLowerCase() === 'route' || !sawHideMileMarkers) {
        next.hideMileMarkers = entry.hideMileMarkers;
      }
      sawHideMileMarkers = true;
    }
  }

  try {
    localStorage.setItem(ROUTE_STYLE_STORAGE_KEY, JSON.stringify(next));
  } catch {}
  applyOverlayLinesFromUser(user.routeStyleSettings);
  applyHikeListStylesFromUser(user.routeStyleSettings);
  suppressNextPush = true;
  emitter.emit('route-style-settings:changed', { ...next });
  // The flag is consumed inside `route-style-settings:changed` so we don't
  // hold it longer than necessary — clear on next microtask as a safety net
  // for listeners that defer their own work.
  Promise.resolve().then(() => { suppressNextPush = false; });
}

/**
 * Hydrates the overlay line styles from the same `routeStyleSettings` payload.
 *
 * Split out from `applyFromUser` only because the two families key off
 * different `route_type` strings and land in different localStorage buckets —
 * they share one GetUser round trip. Entries whose `route_type` isn't an
 * `overlay_*` key are skipped here (and vice versa in `applyFromUser`), which
 * is also what makes an older client safe: it simply sees keys it can't map.
 */
function applyOverlayLinesFromUser(entries: CloudRouteStyleSetting[] | null | undefined) {
  if (!entries || entries.length === 0) return;

  // Start from current local so an overlay the server has never seen keeps its
  // existing local value rather than snapping back to default.
  const next: OverlayLineStyles = getOverlayLineStyles();
  let sawAny = false;

  for (const entry of entries) {
    const type = overlayTypeForCloudType(entry.routeType);
    if (!type) continue;
    sawAny = true;
    const prefs = { ...next[type] };
    if (entry.color != null) prefs.color = entry.color;
    if (entry.thicknessMultiplier != null) prefs.thickness = entry.thicknessMultiplier;
    if (entry.opacity != null) prefs.opacity = opacityFromCloud(entry.opacity);
    next[type] = prefs;
  }

  if (!sawAny) return;
  saveOverlayLineStyles(next);
  suppressNextOverlayPush = true;
  emitter.emit('overlay-line-styles:changed', { ...next });
  Promise.resolve().then(() => { suppressNextOverlayPush = false; });
}

/**
 * Hydrates the "Hike lists" overlay's per-list styles, stored under
 * `overlay_hike_list_<id>` in the same payload. Emits its own `:hydrated`
 * event (not `:changed`), so applying server values never pushes them back.
 */
function applyHikeListStylesFromUser(entries: CloudRouteStyleSetting[] | null | undefined) {
  const { styles, changed } = hikeListStylesFromCloud(entries, readHikeListStyles());
  if (!changed) return;
  writeHikeListStyles(styles);
  emitter.emit('hike-list-styles:hydrated');
}

/**
 * Hydrates the Hike lists overlay's on/off and list selection from
 * `User.hikeListOverlaySettings` (server wins). Null — never synced — keeps the
 * local state. Emits `hike-list-overlay:hydrated`, which the overlays store
 * applies without pushing it back.
 */
export function applyHikeListOverlaySettingsFromUser(
  user: { hikeListOverlaySettings?: CloudHikeListOverlaySettings | null } | null | undefined,
) {
  const settings = hikeListOverlaySettingsFromCloud(user?.hikeListOverlaySettings);
  if (!settings || sameHikeListOverlaySettings(settings, readHikeListOverlaySettings())) return;
  writeHikeListOverlaySettings(settings);
  emitter.emit('hike-list-overlay:hydrated');
}

export function pushHikeListOverlaySettings(settings: HikeListOverlaySettings) {
  graphClient
    .request(UpdateUser, { user: { hikeListOverlaySettings: hikeListOverlaySettingsToCloud(settings) } })
    .catch((err: unknown) => console.warn('[routeStyleSettingsCloudSync] UpdateUser (hike lists overlay) failed', err));
}

function scheduleHikeListOverlayPush(settings: HikeListOverlaySettings) {
  pendingHikeListOverlay = settings;
  if (hikeListOverlayDebounce != null) window.clearTimeout(hikeListOverlayDebounce);
  hikeListOverlayDebounce = window.setTimeout(flushHikeListOverlayPush, 500);
}

function flushHikeListOverlayPush() {
  if (hikeListOverlayDebounce != null) window.clearTimeout(hikeListOverlayDebounce);
  hikeListOverlayDebounce = null;
  const settings = pendingHikeListOverlay;
  pendingHikeListOverlay = null;
  if (settings) pushHikeListOverlaySettings(settings);
}

export function pushAll() {
  const current = getRouteStyleSettings();
  const inputs: CloudRouteStyleSetting[] = (Object.keys(WEB_TO_CLOUD) as WebTab[]).map((tab) => {
    const prefs = current[tab];
    return {
      routeType: cloudTypeForWebTab(tab),
      color: prefs.color,
      thicknessMultiplier: prefs.thickness,
      opacity: opacityToCloud(prefs.opacity),
      // Write the same global to every type so platforms with per-type UIs
      // (iOS) see a consistent value regardless of which one they read.
      hideMileMarkers: current.hideMileMarkers,
    };
  });

  // Overlay line styles share the column, so they go out in the same mutation.
  // `hideMileMarkers` is deliberately omitted — overlay lines have no mile
  // markers, and sending it would put a stray flag on those keys for the
  // platforms that read the global from whichever entry they find first.
  const overlays = getOverlayLineStyles();
  for (const type of OVERLAY_LINE_TYPES) {
    const prefs = overlays[type];
    inputs.push({
      routeType: cloudTypeForOverlayType(type),
      color: prefs.color,
      thicknessMultiplier: prefs.thickness,
      opacity: opacityToCloud(prefs.opacity),
    });
  }

  // One entry per hike list (`overlay_hike_list_<id>`). The server merges per
  // key, and iOS/Android skip keys they don't know, so these are safe to send.
  inputs.push(...hikeListStylesToCloud(readHikeListStyles()));

  graphClient
    .request(UpdateUser, { user: { routeStyleSettings: inputs } })
    .catch((err: unknown) => console.warn('[routeStyleSettingsCloudSync] UpdateUser failed', err));
}

function schedulePush() {
  if (debounceHandle != null) {
    window.clearTimeout(debounceHandle);
  }
  debounceHandle = window.setTimeout(() => {
    debounceHandle = null;
    pushAll();
  }, 500);
}

/**
 * Cancels the debounce and fires the push synchronously. Used by the
 * page-hide handler so a quick edit → tab switch / close doesn't drop
 * the push, which is exactly the failure mode QA was hitting where
 * web → mobile sync looked broken: setTimeout is throttled in hidden
 * tabs and killed on unload, so the queued UpdateUser never went out.
 */
function flushPendingPush() {
  flushHikeListOverlayPush();
  if (debounceHandle == null) return;
  window.clearTimeout(debounceHandle);
  debounceHandle = null;
  pushAll();
}

export function init() {
  if (initialised) return;
  initialised = true;

  // `user:updated` only fires on login / logout — not when the page loads
  // with an already-signed-in session. So we also trigger a hydrate the
  // moment Firebase has restored its auth state.
  import('./firebase/authReady').then(({ authReady }) => {
    authReady.then(() => { void hydrateFromServer(); });
  });

  // On every user fetch the navbar/onboarding modules emit `user:updated`.
  // `window.user` is a Firebase `User`, not the GraphQL one, so we trigger a
  // GetUser ourselves rather than trying to dig the payload out of the bus.
  emitter.on('user:updated', () => {
    void hydrateFromServer();
  });

  // CustomiseRouteStyleView emits this on every persist(). Skip when we
  // emitted it ourselves from applyFromUser to avoid an immediate round
  // trip of the same data.
  emitter.on('route-style-settings:changed', () => {
    if (suppressNextPush) {
      suppressNextPush = false;
      return;
    }
    schedulePush();
  });

  // CustomiseOverlayLineStyleView emits this on every persist(). Same debounce
  // and same mutation as the route styles above.
  emitter.on('overlay-line-styles:changed', () => {
    if (suppressNextOverlayPush) {
      suppressNextOverlayPush = false;
      return;
    }
    schedulePush();
  });

  // The overlays store emits this when a hike list's style changes (an edit,
  // a reset, or a colour handed to a list seen for the first time).
  emitter.on('hike-list-styles:changed', () => {
    schedulePush();
  });

  // The overlays store emits this when the user switches the Hike lists overlay
  // or picks lists — never for a value that arrived from the account.
  emitter.on('hike-list-overlay:changed', (settings: HikeListOverlaySettings) => {
    scheduleHikeListOverlayPush(settings);
  });

  // Drain the debounce when the tab is hidden / closing. Browsers throttle
  // setTimeout in hidden tabs (to >= 1s) and kill it outright on unload,
  // so a user who picks a color and immediately switches tabs / closes
  // the window can otherwise drop the queued UpdateUser. visibilitychange
  // fires before unload and gives the in-flight fetch a chance to land.
  // pagehide is the canonical "page is going away" event (covers bfcache
  // restores too) — we listen to both so neither browser-specific quirk
  // catches us out.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushPendingPush();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushPendingPush);
  }
}

export async function hydrateFromServer() {
  try {
    // window.user is the *backend* user object (no `uid` field) — the
    // Firebase uid lives on firebase.auth().currentUser. Wait for authReady
    // first so we don't race the Firebase SDK restoring its session from
    // localStorage on a fresh page load.
    await authReady;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    const data: any = await graphClient.request(GetUser, { firebaseID: uid });
    applyFromUser(data?.user);
    applyHikeListOverlaySettingsFromUser(data?.user);
  } catch (err) {
    console.warn('[routeStyleSettingsCloudSync] GetUser failed', err);
  }
}

// Pure boundary mapping helpers live in routeStyleSettingsCloudMapping.ts
// (re-exported here so test specs that mock the GraphQL imports can still
// reach them via the original module path).
export {
  cloudTypeForWebTab,
  opacityFromCloud,
  opacityToCloud,
  webTabForCloudType,
} from './routeStyleSettingsCloudMapping';
